import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledBanner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: ${colors.blue[50]};
    border-bottom-left-radius: 100px;
    border-bottom-right-radius: 100px;
    z-index: -1;
  }
  @media ${devices.laptopM} {
    padding-left: ${spaces.large2X}px;
    padding-right: ${spaces.large2X}px;
    &::before {
      border-bottom-left-radius: 32px;
      border-bottom-right-radius: 32px;
    }
  }
  @media ${devices.tablet} {
    &::before {
      border-bottom-left-radius: 32px;
      border-bottom-right-radius: 32px;
    }
  }
`;

const StyledBannerContainer = styled.div`
  display: flex;
  justify-content: space-between;

  position: relative;

  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  padding-top: ${spaces.large2X}px;
  padding-bottom: ${spaces.large2X}px;

  @media ${devices.tablet} {
    padding-left: ${spaces.regular}px;
    padding-right: ${spaces.regular}px;
    padding-top: ${spaces.regular}px;
    padding-bottom: 0;
  }
`;

const StyledBannerImageContainer = styled.div`
  ${({ width }) =>
    width &&
    css`
      img {
        width: ${width}px;
      }
    `}

  @media ${devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledMobileBannerImageContainer = styled(
  forwardRef(({ hideImageMobile, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: none;
  text-align: center;

  img {
    width: 50%;
  }

  @media ${devices.tablet} {
    display: block;
    margin-bottom: ${spaces.regular}px;
    text-align: center;

    ${({ hideImageMobile }) =>
      hideImageMobile &&
      css`
        display: none;
      `}
  }
`;

const StyledBannerContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  padding-right: 12%;

  @media ${devices.tablet} {
    padding-right: 0;
  }
`;

const StyledBannerTitle = styled.div`
  margin-bottom: ${spaces.regular}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-size: ${fontSizes.large8X}px;
  font-weight: 500;
  color: ${colors.gray[800]};

  @media ${devices.tablet} {
    text-align: center;
    font-size: ${fontSizes.large2X}px;
    text-align: center;
  }
`;

const StyledBannerText = styled.div`
  margin-bottom: ${spaces.regular}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-size: ${fontSizes.large}px;
  font-weight: 400;
  color: ${colors.gray[700]};

  a {
    color: ${colors.blue[500]};
  }

  @media ${devices.tablet} {
    display: none;
    text-align: center;
    font-size: ${fontSizes.regular}px;
    text-align: center;
  }
`;

const StyledBulletsContainer = styled.div`
  margin-bottom: ${spaces.largeX + 2}px;
`;

const StyledBullet = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spaces.small - 2}px;
`;

const StyledBulletIconContainer = styled.div`
  margin-right: ${spaces.small}px;
`;

const StyledBulletText = styled.div`
  flex-grow: 1;
  font-family: 'Aeonik';
  font-style: normal;
  font-size: ${fontSizes.large}px;
  font-weight: 400;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
  }
`;

const StyledButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  @media ${devices.tablet} {
    justify-content: center;
  }
`;

const StyledButtonContainer = styled.div`
  margin-right: ${spaces.regular}px;
  margin-bottom: ${spaces.regular}px;

  &:last-child {
    margin-right: 0;
  }

  @media ${devices.tablet} {
    margin-right: ${spaces.small}px;
  }
`;

const StyledButtonHint = styled.div`
  margin-top: ${spaces.small2X}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-size: ${fontSizes.smallX}px;
  font-weight: 400;
  color: ${colors.gray[500]};
  text-align: center;
`;

const BaseBannerSection = ({
  hideImageMobile,
  imgSrc,
  title,
  text,
  bullets,
  defaultBulletIcon,
  buttons,
}) => {
  const isBullets = Boolean(bullets?.length);
  const isButtons = Boolean(buttons?.length);

  return (
    <StyledBanner>
      <StyledBannerContainer>
        <StyledBannerContentContainer>
          <StyledBannerTitle>{title}</StyledBannerTitle>
          <StyledBannerText>{text}</StyledBannerText>
          {isBullets && (
            <StyledBulletsContainer>
              {bullets.map((bullet, idx) => {
                const { icon, text } = bullet;

                if (text) {
                  return (
                    <StyledBullet key={`bullet:${idx}`}>
                      {icon && (
                        <StyledBulletIconContainer>
                          {icon}
                        </StyledBulletIconContainer>
                      )}
                      <StyledBulletText>{text}</StyledBulletText>
                    </StyledBullet>
                  );
                }

                return (
                  <StyledBullet key={`bullet:${idx}`}>
                    <StyledBulletIconContainer>
                      {defaultBulletIcon || (
                        <Icon
                          alias={ICONS_ALIASES.checkRounded}
                          color={colors.blue[500]}
                        />
                      )}
                    </StyledBulletIconContainer>
                    <StyledBulletText>{bullet}</StyledBulletText>
                  </StyledBullet>
                );
              })}
            </StyledBulletsContainer>
          )}
          <StyledMobileBannerImageContainer hideImageMobile={hideImageMobile}>
            <picture>
              <source srcSet={imgSrc} type="image/svg" />
              <img src={imgSrc} alt="Banner image" />
            </picture>
          </StyledMobileBannerImageContainer>
          {isButtons && (
            <StyledButtonsContainer>
              {buttons.map(({ hint, text, ...restButtonProps }, idx) => (
                <StyledButtonContainer key={`button:${idx}`}>
                  <Button {...restButtonProps}>{text}</Button>
                  {hint && <StyledButtonHint>{hint}</StyledButtonHint>}
                </StyledButtonContainer>
              ))}
            </StyledButtonsContainer>
          )}
        </StyledBannerContentContainer>
        <StyledBannerImageContainer
          width={!isBullets && !isButtons ? 230 : undefined}
        >
          <picture>
            <source srcSet={imgSrc} type="image/png" />
            <img src={imgSrc} alt="Banner image" />
          </picture>
        </StyledBannerImageContainer>
      </StyledBannerContainer>
    </StyledBanner>
  );
};

BaseBannerSection.propTypes = {
  hideImageMobile: PropTypes.bool,
  imgSrc: PropTypes.string,
  title: PropTypes.node,
  text: PropTypes.node,
  bullets: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.node,
        text: PropTypes.node,
      })
    ),
  ]),
  defaultBulletIcon: PropTypes.node,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      hint: PropTypes.node,
      text: PropTypes.node,
      color: PropTypes.string,
      disabled: PropTypes.bool,
      large: PropTypes.bool,
      transparent: PropTypes.bool,
      outlined: PropTypes.bool,
      prefix: PropTypes.node,
      suffix: PropTypes.node,
      secondary: PropTypes.bool,
      onClick: PropTypes.func,
    })
  ),
};

export default BaseBannerSection;
